import { useState } from "react";
import { CheckCircle2, Eye, Image as ImagePlaceholder } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import {
  BRANDING_SHELL,
  BRANDING_SHELL_STYLE,
  BRANDING_CARD_PADDING,
  BRANDING_TILE_RADIUS,
  BRANDING_LABEL,
} from "../branding.tokens";
import { BRANDING_ASSET_KINDS, BRANDING_ASSET_META } from "../constants";
import BrandingSectionHeader from "./BrandingSectionHeader";

// Same registry BrandingAssetUploads reads, in the same order, so "what's
// live" here can't drift from what that page lets you manage.
const ASSET_ORDER = Object.values(BRANDING_ASSET_KINDS);

const PublishedAssetThumb = ({ kind, url, onPreview }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={`group relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-md border border-border bg-background-secondary/50 ${url ? "cursor-pointer" : ""}`}
      onClick={url ? () => onPreview(kind, url) : undefined}
      role={url ? "button" : undefined}
      aria-label={url ? `Preview ${BRANDING_ASSET_META[kind]?.label || kind}` : undefined}
    >
      {url ? (
        <>
          <img
            src={url}
            alt={`${BRANDING_ASSET_META[kind]?.label || kind} preview`}
            className="max-h-7 max-w-7 object-contain"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute inset-0 hidden items-center justify-center bg-background-primary/60 group-hover:flex">
            <Eye className="h-3.5 w-3.5 text-text-primary" aria-hidden="true" />
          </div>
        </>
      ) : (
        <ImagePlaceholder className="h-3.5 w-3.5 text-text-secondary/40" aria-hidden="true" />
      )}
    </div>
    <span className="text-[9px] font-medium text-text-secondary">{BRANDING_ASSET_META[kind]?.label || kind}</span>
  </div>
);

const BrandingPublishedPanel = ({ published, publishedAssets }) => {
  const [preview, setPreview] = useState(null);

  return (
    <div className={`${BRANDING_SHELL} ${BRANDING_CARD_PADDING}`} style={BRANDING_SHELL_STYLE}>
      <BrandingSectionHeader
        icon={CheckCircle2}
        title="Published Branding"
        subtitle="Currently live for tenant-facing surfaces."
      />

      {published ? (
        <div className={`${BRANDING_TILE_RADIUS} bg-background-secondary/30 p-3`} style={BRANDING_SHELL_STYLE}>
          <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
            <div>
              <p className={BRANDING_LABEL}>Display name</p>
              <p className="mt-0.5 font-medium text-text-primary">{published?.config?.displayName || "-"}</p>
            </div>
            <div>
              <p className={BRANDING_LABEL}>Version</p>
              <p className="mt-0.5 font-medium text-text-primary">v{published?.version || 0}</p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-3" style={BRANDING_SHELL_STYLE}>
            {ASSET_ORDER.map((kind) => (
              <PublishedAssetThumb
                key={kind}
                kind={kind}
                url={publishedAssets?.[kind]?.url || null}
                onPreview={(k, url) => setPreview({ kind: k, url })}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={`${BRANDING_TILE_RADIUS} border border-dashed border-border bg-background-secondary/20 px-3 py-3 text-xs text-text-secondary`}>
          No published branding yet.
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-lg" showCloseButton>
          <DialogHeader>
            <DialogTitle>{preview ? BRANDING_ASSET_META[preview.kind]?.label || preview.kind : ""}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-6">
            {preview && (
              <img
                src={preview.url}
                alt={`${BRANDING_ASSET_META[preview.kind]?.label || preview.kind} preview`}
                className="max-h-[60vh] max-w-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandingPublishedPanel;
